import { MAX_FLOW_CONFIG_KEY_SIZE, MAX_FLOW_CONFIG_SIZE } from "constants/flowDesigner";

export const CONFIGURATION_TYPES = [
   { label: "boolean", value: "boolean" },
   { label: "number", value: "number" },
   { label: "object", value: "object" },
   { label: "string", value: "string" }
];

export const CONFIGURATION_KEY_PATTERN = /^[a-z][a-z0-9_]*$/;
export const RESERVED_CONFIGURATION_KEYS = new Set(["__proto__", "constructor", "prototype"]);
export const VALUE_SIZE_LIMITED_TYPES = ["string", "object", "array"];

export const createConfigurationField = () => ({ key: "", value: "", type: "string" });
export const cloneConfiguration = (configuration) => JSON.parse(JSON.stringify(configuration));
export const toFieldString = (value) => (value === null || value === undefined ? "" : String(value));
export const isValueSizeLimited = (field) => VALUE_SIZE_LIMITED_TYPES.includes(field.type);

export const toConfigurationKey = (value) =>
   value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .replace(/^[^a-z]+/, "");

export const getFlowSettings = (flow) => {
   if (!flow?.flow_settings) return {};
   if (typeof flow.flow_settings === "object") return flow.flow_settings;

   try {
      return JSON.parse(flow.flow_settings);
   } catch {
      return {};
   }
};

export const getTransmittableFlow = (flow, flowSettings) => ({
   id: flow.id,
   name: flow.name,
   created_by: flow.created_by,
   created_by_id: flow.created_by_id,
   platform_name: flow.platform_name,
   platform_type: flow.platform_type,
   notes: flow.notes,
   group_id: flow.group_id,
   group_name: flow.group_name,
   shared_status: flow.shared_status,
   shared_link: flow.shared_link,
   trashed: flow.trashed,
   flow_settings: JSON.stringify(flowSettings),
   integrations: flow.integrations,
   current_version: flow.current_version,
   description: flow.description,
   credentials: flow.credentials,
   connectors: flow.connectors,
   steps: flow.steps
});

export const configurationToFields = (values = {}) =>
   Object.entries(values).map(([key, value]) => ({
      key,
      value: typeof value === "object" && value !== null ? JSON.stringify(value, null, 2) : value,
      type: Array.isArray(value) ? "object" : typeof value
   }));

export const fieldsToConfiguration = (fields) => {
   const values = {};

   fields.forEach((field) => {
      if (!field.key) return;

      if (field.type === "number") values[field.key] = Number(field.value);
      else if (field.type === "object") values[field.key] = field.value ? JSON.parse(field.value) : {};
      else values[field.key] = field.value;
   });

   return values;
};

export const getConfigurationValueSize = (field) => {
   const value = toFieldString(field.value);

   if (field.type !== "object" && field.type !== "array") return value.length;

   try {
      return JSON.stringify(JSON.parse(value)).length;
   } catch {
      return value.length;
   }
};

export const isConfigurationKeyOverLimit = (field) => toFieldString(field.key).length > MAX_FLOW_CONFIG_KEY_SIZE;
export const isConfigurationValueOverLimit = (field) => isValueSizeLimited(field) && getConfigurationValueSize(field) > MAX_FLOW_CONFIG_SIZE;
export const isConfigurationKeyMissing = (field) => !toFieldString(field.key).trim();
export const isConfigurationTypeMissing = (field) => !field.type;
export const isConfigurationValueMissing = (field) => !["boolean", "string"].includes(field.type) && !toFieldString(field.value).trim();
export const isConfigurationNumberInvalid = (field) => field.type === "number" && !Number.isFinite(Number(field.value));
export const isConfigurationObjectInvalid = (field) => {
   if (field.type !== "object") return false;

   try {
      const value = JSON.parse(field.value);
      return value === null || Array.isArray(value) || typeof value !== "object";
   } catch {
      return true;
   }
};

export const isConfigurationFieldInvalid = (field, { allowEmptyString = true, protectReservedKeys = false } = {}) => {
   if (isConfigurationKeyMissing(field) || isConfigurationTypeMissing(field)) return true;
   if (protectReservedKeys && RESERVED_CONFIGURATION_KEYS.has(field.key)) return true;
   if (!allowEmptyString && !toFieldString(field.value).trim()) return true;

   return isConfigurationValueMissing(field) || isConfigurationNumberInvalid(field) || isConfigurationObjectInvalid(field);
};
